import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "@shared/hooks/use-toast";
import {
  useAssignmentTypeList,
  useEmployeeList,
  useSubAreaList,
  useassignDistributor,
} from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface AssignDistributionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedAreas: string[];
  onSuccess: () => void;
}

const AssignDistributionModal: React.FC<AssignDistributionModalProps> = ({
  open,
  onOpenChange,
  selectedAreas,
  onSuccess,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: subareaList } = useSubAreaList(open);
  const { data: employeeListData } = useEmployeeList(open);
  const { data: assignmentTypeList } = useAssignmentTypeList(open);
  const { mutateAsync: assignDistributor } = useassignDistributor();

  const employeeList = useMemo(
    () =>
      employeeListData?.results?.map((employee) => ({
        value: employee.id,
        label: employee.name,
      })),
    [employeeListData]
  );

  const subareas = useMemo(
    () =>
      subareaList?.map((subarea: any) => ({
        value: subarea.id,
        label: subarea.name,
      })),
    [subareaList]
  );

  const assignmentTypes = useMemo(
    () =>
      assignmentTypeList?.result?.map((assignmentType: any) => ({
        value: assignmentType.key,
        label: assignmentType.value,
      })),
    [assignmentTypeList]
  );

  // Single showWhen function using switch case
  const showWhenCondition = (fieldName: string) => {
    return (formValues: any) => {
      const assignmentType = formValues.assignmentType;

      switch (assignmentType) {
        case "0":
          return fieldName === "employee";

        case "1":
          return (
            fieldName === "courierPostalServiceName" ||
            fieldName === "dispatchDate"
          );

        default:
          return false;
      }
    };
  };

  // Form fields definition
  const formFields: FormField[] = [
    {
      name: "assignmentType",
      type: "select",
      label: "Assignment Type",
      placeholder: "Select assignment type",
      required: true,
      options: assignmentTypes,
      className: "col-span-2",
    },
    {
      name: "employee",
      type: "select",
      label: "Select Employee",
      placeholder: "Choose employee",
      required: false,
      options: employeeList,
      showWhen: showWhenCondition("employee"),
      className: "col-span-2",
    },
    {
      name: "courierPostalServiceName",
      type: "text",
      label: "Courier/Postal Service Name",
      placeholder: "Courier/Postal Service Name",
      required: false,
      showWhen: showWhenCondition("courierPostalServiceName"),
      className: "col-span-2",
    },
    {
      name: "dispatchDate",
      type: "datepicker",
      label: "Dispatch Date (Optional)",
      placeholder: "Dispatch Date (Optional)",
      showWhen: showWhenCondition("dispatchDate"),
      required: false,
      className: "col-span-2",
    },
  ];

  // Create form with proper default values
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      assignmentType: [],
      employee: [],
      note: "",
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset();
    }
  }, [open]);

  // Handle form submission
  const handleSubmit = async (formValues: any) => {
    switch (formValues.assignmentType) {
      case "0":
        const payloadInHouse = {
          sub_area: selectedAreas,
          assignment_type: Number(formValues.assignmentType),
          remote_utility_id: Number(remoteUtilityId),
          remote_assigned_user_id: Number(formValues.employee),
          remote_assigned_user_name: employeeList.find(
            (emp) => String(emp.value) === String(formValues.employee)
          )?.label,
        };

        await assignDistributor(payloadInHouse);
        onSuccess();
        handleClose();
        break;

      case "1":
        const payloadExternal: any = {
          sub_area: selectedAreas,
          assignment_type: Number(formValues.assignmentType),
          remote_utility_id: Number(remoteUtilityId),
          service_provider: formValues.courierPostalServiceName,
        };

        // ✅ Add only when dispatch_date exists & not empty
        if (formValues.dispatchDate) {
          payloadExternal.dispatch_date = formValues.dispatchDate;
        }

        await assignDistributor(payloadExternal);
        onSuccess();
        handleClose();
        break;

      default:
        break;
    }
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Assign Distribution</DialogTitle>
          <DialogDescription>
            Choose assignment method for the selected sub-area
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: "Assign",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                buttonContainer: "flex justify-end gap-3",
                input:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                select:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
                number:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                "multi-select":
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignDistributionModal;
